import React, { useState } from "react";
import { FaMapMarkerAlt, FaChevronRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import "./Location.css";

const CITIES = [
  { name: "Maple Valley", lat: 47.3668, lng: -122.042 },
  { name: "Black Diamond", lat: 47.3109, lng: -122.0034 },
  { name: "Covington", lat: 47.3589, lng: -122.1222 },
  { name: "Enumclaw", lat: 47.2043, lng: -121.9916 },
  { name: "Kent", lat: 47.3809, lng: -122.2348 },
  { name: "Auburn", lat: 47.3073, lng: -122.2285 },
  { name: "Renton", lat: 47.4829, lng: -122.2171 },
  { name: "Fairwood", lat: 47.4484, lng: -122.1576 },
  { name: "Puyallup", lat: 47.1854, lng: -122.2929 },
  { name: "Tacoma", lat: 47.2529, lng: -122.4443 },
  { name: "Seattle", lat: 47.6062, lng: -122.3321 },
  { name: "Kirkland", lat: 47.6815, lng: -122.2087 },
  { name: "Newcastle", lat: 47.5389, lng: -122.1557 },
  { name: "Issaquah", lat: 47.5301, lng: -122.0326 },
  { name: "Tukwila", lat: 47.4626, lng: -122.255 },
  { name: "Federal Way", lat: 47.3223, lng: -122.3126 },
  { name: "Ravensdale", lat: 47.3551, lng: -121.9837 },
  { name: "Hobart", lat: 47.4218, lng: -121.9729 },
  { name: "Fife", lat: 47.2393, lng: -122.3579 },
  { name: "Edgewood", lat: 47.2501, lng: -122.2937 },
];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "var(--border-radius)",
};

const center = {
  lat: 47.35,
  lng: -122.2,
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemAnimation = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export default function Location() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [selectedCity, setSelectedCity] = useState(null);

  if (loadError) return <div className="map-error">Error loading maps</div>;
  if (!isLoaded) return <div className="map-loading">Loading maps</div>;

  const handleCityClick = (city) => {
    setSelectedCity(city);
  };

  return (
    <section className="location-section" id="locations">
      <div className="location-container">
        {/* Decorative elements */}
        <div className="location-decoration location-decoration-1"></div>
        <div className="location-decoration location-decoration-2"></div>
        <div className="location-decoration location-decoration-3"></div>

        <motion.header
          className="location-header"
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="section-title-container">
            <div className="section-divider"></div>
            <h2 className="section-subtitle">SERVING THE PACIFIC NORTHWEST</h2>
            <div className="section-divider"></div>
          </div>
          <h2 className="location-title">
            Primed Landscaping <span>Service Areas</span>
          </h2>
          <p className="location-subtitle">
            We serve the greater Seattle region with full-service landscaping,
            including lawn care and hardscaping. Many companies offer similar
            services, but we set a higher bar. At Primed Landscaping, we treat
            your property with care, pay attention to the details, and do the
            work right.
          </p>
        </motion.header>

        <div className="location-content">
          {/* Google Map Visualization */}
          <motion.div
            className="location-map"
            initial="hidden"
            whileInView="visible"
            variants={fadeIn}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: 0.2 }}
          >
            <div className="map-wrapper">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={10}
                center={
                  selectedCity
                    ? { lat: selectedCity.lat, lng: selectedCity.lng }
                    : center
                }
                options={{
                  // ✅ merged into one styles array (fixes duplicate key)
                  styles: [
                    {
                      featureType: "poi",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "transit",
                      elementType: "labels.icon",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "administrative",
                      elementType: "geometry",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "water",
                      elementType: "labels.text",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "road",
                      elementType: "geometry",
                      stylers: [{ color: "#f5f5f5" }],
                    },
                    {
                      featureType: "road",
                      elementType: "labels.icon",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "landscape",
                      elementType: "geometry",
                      stylers: [{ color: "#f8f9fa" }],
                    },
                  ],
                  disableDefaultUI: true,
                  zoomControl: true,
                }}
              >
                {CITIES.map((city) => (
                  <Marker
                    key={city.name}
                    position={{ lat: city.lat, lng: city.lng }}
                    icon={{
                      url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                      scaledSize: new window.google.maps.Size(32, 32),
                    }}
                    onClick={() => handleCityClick(city)}
                  />
                ))}
              </GoogleMap>

              <div className="map-overlay"></div>
              <div className="map-label">
                <FaMapMarkerAlt className="map-pin-icon" />
                <span>Seattle Metro Area</span>
              </div>
            </div>
          </motion.div>

          {/* Cities List */}
          <motion.div
            className="location-list-container"
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="location-list-header">
              <h3 className="list-title">Cities We Serve</h3>
              <p className="list-subtitle">Click on a city to see it on the map</p>
              <div className="list-divider"></div>
            </div>

            <motion.ul className="location-list">
              <AnimatePresence>
                {CITIES.map((city) => (
                  <motion.li
                    key={city.name}
                    className={`location-item ${
                      selectedCity?.name === city.name ? "active" : ""
                    }`}
                    variants={itemAnimation}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onClick={() => handleCityClick(city)}
                    whileHover={{
                      y: -3,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.98 }}
                    layout
                  >
                    <div className="item-content">
                      <FaMapMarkerAlt className="location-icon" />
                      <span className="city-name">{city.name}</span>
                    </div>
                    <div className="item-hover-effect"></div>
                    <FaChevronRight className="item-arrow" />
                  </motion.li>
                ))}
              </AnimatePresence>
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
